import type { RenderContext } from '../render/context/createRenderContext';
import type { NodeLike } from '../render/context/node';
import { Provider } from './provider';

/**
 * 属性分组定义
 * 不同的属性变化会影响不同的渲染阶段
 */
export const DIRTY_GROUPS = {
  /** 变换相关：位置、尺寸、旋转 */
  transform: ['x', 'y', 'w', 'h', 'rotation', 'scaleX', 'scaleY'],
  /** 样式相关：填充、描边、透明度等 */
  style: [
    'fill',
    'stroke',
    'strokes',
    'strokeWidth',
    'opacity',
    'shadow',
    'blur',
    'cornerRadius',
    'radius',
    'background',
    'borderWidth',
    'borderColor',
  ],
  /** 内容相关：文本、图片源等 */
  content: [
    'text',
    'src',
    'highlights',
    'styles',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'lineHeight',
    'textAlign',
    'alignItems',
    'textDecoration',
    'fillMode',
    'textFx',
    'padding',
  ],
  /** 布局相关：父子关系、层级 */
  layout: ['parentId', 'index'],
  /** 效果相关：滤镜、混合模式等 */
  effects: ['filter', 'blendMode', 'clipPath', 'effect'],
} as const;

export type DirtyGroup = keyof typeof DIRTY_GROUPS;

/**
 * 节点脏状态
 */
export interface NodeDirtyState {
  /** 各分组的版本号 */
  groups: Record<DirtyGroup, number>;
  /** 上次更新时间戳 */
  timestamp: number;
  /** 复合版本字符串，用于缓存键 */
  version: string;
}

/**
 * 渲染优先级
 */
export enum RenderPriority {
  /** 高优先级：用户交互中的节点（拖动、编辑） */
  HIGH = 0,
  /** 普通优先级：普通更新 */
  NORMAL = 1,
  /** 低优先级：视口外的节点 */
  LOW = 2,
}

/**
 * 渲染任务
 */
export interface RenderTask {
  nodeId: string;
  priority: RenderPriority;
  timestamp: number;
  groups: DirtyGroup[];
}

/**
 * 脏检测 API
 */
export interface DirtyAPI {
  /** 标记节点为脏 */
  markDirty: (nodeId: string, changedKeys?: string[]) => void;
  /** 批量标记节点为脏 */
  markDirtyBatch: (nodeIds: string[], changedKeys?: string[]) => void;
  /** 获取节点的脏状态 */
  getDirtyState: (nodeId: string) => NodeDirtyState | null;
  /** 获取节点的版本字符串 */
  getVersion: (nodeId: string) => string;
  /** 获取节点特定分组的版本号 */
  getGroupVersion: (nodeId: string, group: DirtyGroup) => number;
  /** 获取内容相关的版本键（content + style） */
  getContentKey: (nodeId: string) => string;
  /** 获取变换相关的版本键（transform） */
  getTransformKey: (nodeId: string) => string;
  /** 获取节点及其所有子节点的聚合版本 */
  getSubtreeVersion: (nodeId: string, childIds: string[]) => string;
  /** 检查子树中是否有任何节点变脏 */
  isSubtreeDirty: (nodeId: string, childIds: string[], cachedVersion: string) => boolean;
  /** 获取子树中变脏的节点 ID 列表 */
  getDirtyChildIds: (childIds: string[], cachedVersions: Map<string, string>) => string[];
  /** 检查节点是否在指定分组中有变化 */
  isGroupDirty: (nodeId: string, group: DirtyGroup, sinceVersion?: number) => boolean;
  /** 清除节点的脏状态 */
  clearDirty: (nodeId: string) => void;
  /** 清除所有脏状态 */
  clearAllDirty: () => void;
  /** 获取属性对应的分组，未知属性默认归类到 content */
  getPropertyGroup: (key: string) => DirtyGroup;
  /** 设置节点为高优先级（正在交互） */
  setHighPriority: (nodeId: string) => void;
  /** 移除节点的高优先级状态 */
  clearHighPriority: (nodeId: string) => void;
  /** 清除所有高优先级状态 */
  clearAllHighPriority: () => void;
  /** 检查节点是否为高优先级 */
  isHighPriority: (nodeId: string) => boolean;
  /** 获取自上次渲染以来变脏的节点 */
  getDirtyNodesSinceLastRender: () => string[];
  /** 获取按优先级排序的脏节点 */
  getSortedDirtyNodes: (viewportNodeIds?: Set<string>) => string[];
  /** 标记渲染完成 */
  markRenderComplete: () => void;
  /** 检查是否有待渲染的脏节点 */
  hasDirtyNodes: () => boolean;
  /** 获取脏节点统计信息 */
  getDirtyStats: () => { total: number; high: number; normal: number; low: number };
}

/**
 * 脏检测 Provider
 *
 * 提供细粒度的节点脏标记机制，支持：
 * 1. 属性级别的脏标记分组
 * 2. 高效的版本号生成
 * 3. 缓存友好的版本字符串
 */
export class DirtyProvider extends Provider {
  /** 节点脏状态映射 */
  private dirtyMap: Map<string, NodeDirtyState> = new Map();

  /** 属性到分组的映射缓存 */
  private propertyGroupCache: Map<string, DirtyGroup> = new Map();

  /** 全局版本计数器，用于生成唯一版本 */
  private globalCounter: number = 0;

  /** 渲染队列 */
  private renderQueue: Map<string, RenderTask> = new Map();

  /** 上次渲染的时间戳 */
  private lastRenderTimestamp: number = 0;

  /** 高优先级节点集合（正在交互的节点） */
  private highPriorityNodes: Set<string> = new Set();

  constructor() {
    super();
    this.buildPropertyGroupCache();
  }

  /**
   * 构建属性到分组的映射缓存
   */
  private buildPropertyGroupCache(): void {
    for (const [group, keys] of Object.entries(DIRTY_GROUPS)) {
      for (const key of keys) {
        this.propertyGroupCache.set(key, group as DirtyGroup);
      }
    }
  }

  /**
   * 获取属性对应的分组
   * 未知属性默认归类到 content 分组，确保缓存能够正确失效
   */
  getPropertyGroup(key: string): DirtyGroup {
    return this.propertyGroupCache.get(key) || 'content';
  }

  /**
   * 创建初始脏状态
   */
  private createInitialState(): NodeDirtyState {
    return {
      groups: {
        transform: 0,
        style: 0,
        content: 0,
        layout: 0,
        effects: 0,
      },
      timestamp: Date.now(),
      version: '0-0-0-0-0',
    };
  }

  /**
   * 生成版本字符串
   */
  private generateVersion(state: NodeDirtyState): string {
    const { groups } = state;
    return `${groups.transform}-${groups.style}-${groups.content}-${groups.layout}-${groups.effects}`;
  }

  /**
   * 获取或创建节点的脏状态
   */
  private getOrCreateState(nodeId: string): NodeDirtyState {
    let state = this.dirtyMap.get(nodeId);
    if (!state) {
      state = this.createInitialState();
      this.dirtyMap.set(nodeId, state);
    }
    return state;
  }

  /**
   * 标记节点为脏
   * @param nodeId 节点 ID
   * @param changedKeys 变化的属性键名，如果不提供则标记所有分组
   */
  markDirty(nodeId: string, changedKeys?: string[]): void {
    const state = this.getOrCreateState(nodeId);
    const now = Date.now();

    if (!changedKeys || changedKeys.length === 0) {
      // 标记所有分组
      for (const group of Object.keys(state.groups) as DirtyGroup[]) {
        state.groups[group]++;
      }
    } else {
      // 只标记相关分组
      const affectedGroups = new Set<DirtyGroup>();
      for (const key of changedKeys) {
        affectedGroups.add(this.getPropertyGroup(key));
      }

      for (const group of affectedGroups) {
        state.groups[group]++;
      }
    }

    state.timestamp = now;
    state.version = this.generateVersion(state);
    this.globalCounter++;
  }

  /**
   * 批量标记节点为脏
   */
  markDirtyBatch(nodeIds: string[], changedKeys?: string[]): void {
    for (const nodeId of nodeIds) {
      this.markDirty(nodeId, changedKeys);
    }
  }

  /**
   * 获取节点的脏状态
   */
  getDirtyState(nodeId: string): NodeDirtyState | null {
    return this.dirtyMap.get(nodeId) || null;
  }

  /**
   * 获取节点的版本字符串
   * 如果节点没有脏状态，返回初始版本
   */
  getVersion(nodeId: string): string {
    const state = this.dirtyMap.get(nodeId);
    return state?.version || '0-0-0-0-0';
  }

  /**
   * 获取节点特定分组的版本号
   */
  getGroupVersion(nodeId: string, group: DirtyGroup): number {
    const state = this.dirtyMap.get(nodeId);
    return state?.groups[group] || 0;
  }

  /**
   * 检查节点是否在指定分组中有变化
   * @param nodeId 节点 ID
   * @param group 分组名
   * @param sinceVersion 从哪个版本开始检查，如果当前版本大于此值则认为有变化
   */
  isGroupDirty(nodeId: string, group: DirtyGroup, sinceVersion: number = 0): boolean {
    const currentVersion = this.getGroupVersion(nodeId, group);
    return currentVersion > sinceVersion;
  }

  /**
   * 获取内容相关的版本键（content + style + effects）
   * 用于文本/图片内容缓存
   */
  getContentKey(nodeId: string): string {
    const state = this.dirtyMap.get(nodeId);
    if (!state) return '0-0-0';
    return `${state.groups.content}-${state.groups.style}-${state.groups.effects}`;
  }

  /**
   * 获取变换相关的版本键（transform）
   * 用于位置/尺寸变化检测
   */
  getTransformKey(nodeId: string): string {
    const state = this.dirtyMap.get(nodeId);
    if (!state) return '0';
    return `${state.groups.transform}`;
  }

  /**
   * 获取节点及其所有子节点的聚合版本
   * 用于 Frame/Group 缓存失效检测
   * @param nodeId 父节点 ID
   * @param childIds 子节点 ID 列表
   */
  getSubtreeVersion(nodeId: string, childIds: string[]): string {
    const parts: string[] = [this.getVersion(nodeId)];
    for (const childId of childIds) {
      parts.push(this.getVersion(childId));
    }
    return parts.join(':');
  }

  /**
   * 检查子树中是否有任何节点变脏
   * @param nodeId 父节点 ID
   * @param childIds 子节点 ID 列表
   * @param cachedVersion 缓存的版本字符串
   */
  isSubtreeDirty(nodeId: string, childIds: string[], cachedVersion: string): boolean {
    return this.getSubtreeVersion(nodeId, childIds) !== cachedVersion;
  }

  /**
   * 获取子树中变脏的节点 ID 列表
   * @param childIds 子节点 ID 列表
   * @param cachedVersions 缓存的各节点版本映射
   */
  getDirtyChildIds(childIds: string[], cachedVersions: Map<string, string>): string[] {
    const dirtyIds: string[] = [];
    for (const childId of childIds) {
      const cachedVersion = cachedVersions.get(childId);
      const currentVersion = this.getVersion(childId);
      if (cachedVersion !== currentVersion) {
        dirtyIds.push(childId);
      }
    }
    return dirtyIds;
  }

  /**
   * 清除节点的脏状态
   */
  clearDirty(nodeId: string): void {
    this.dirtyMap.delete(nodeId);
  }

  /**
   * 清除所有脏状态
   */
  clearAllDirty(): void {
    this.dirtyMap.clear();
    this.globalCounter = 0;
  }

  /**
   * 获取全局计数器（用于调试）
   */
  getGlobalCounter(): number {
    return this.globalCounter;
  }

  // ============================================
  // 渲染调度相关方法
  // ============================================

  /**
   * 设置节点为高优先级（正在交互）
   */
  setHighPriority(nodeId: string): void {
    this.highPriorityNodes.add(nodeId);
  }

  /**
   * 移除节点的高优先级状态
   */
  clearHighPriority(nodeId: string): void {
    this.highPriorityNodes.delete(nodeId);
  }

  /**
   * 清除所有高优先级状态
   */
  clearAllHighPriority(): void {
    this.highPriorityNodes.clear();
  }

  /**
   * 检查节点是否为高优先级
   */
  isHighPriority(nodeId: string): boolean {
    return this.highPriorityNodes.has(nodeId);
  }

  /**
   * 获取自上次渲染以来变脏的节点
   */
  getDirtyNodesSinceLastRender(): string[] {
    const dirtyNodes: string[] = [];
    for (const [nodeId, state] of this.dirtyMap) {
      if (state.timestamp > this.lastRenderTimestamp) {
        dirtyNodes.push(nodeId);
      }
    }
    return dirtyNodes;
  }

  /**
   * 获取按优先级排序的脏节点
   * @param viewportNodeIds 视口内的节点 ID 集合
   */
  getSortedDirtyNodes(viewportNodeIds?: Set<string>): string[] {
    const dirtyNodes = this.getDirtyNodesSinceLastRender();

    // 按优先级排序
    return dirtyNodes.sort((a, b) => {
      const priorityA = this.getNodePriority(a, viewportNodeIds);
      const priorityB = this.getNodePriority(b, viewportNodeIds);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      // 同优先级按时间戳排序（先变脏的先渲染）
      const stateA = this.dirtyMap.get(a);
      const stateB = this.dirtyMap.get(b);
      return (stateA?.timestamp || 0) - (stateB?.timestamp || 0);
    });
  }

  /**
   * 获取节点的渲染优先级
   */
  private getNodePriority(nodeId: string, viewportNodeIds?: Set<string>): RenderPriority {
    if (this.highPriorityNodes.has(nodeId)) {
      return RenderPriority.HIGH;
    }
    if (viewportNodeIds && !viewportNodeIds.has(nodeId)) {
      return RenderPriority.LOW;
    }
    return RenderPriority.NORMAL;
  }

  /**
   * 标记渲染完成，更新时间戳
   */
  markRenderComplete(): void {
    this.lastRenderTimestamp = Date.now();
  }

  /**
   * 检查是否有待渲染的脏节点
   */
  hasDirtyNodes(): boolean {
    for (const state of this.dirtyMap.values()) {
      if (state.timestamp > this.lastRenderTimestamp) {
        return true;
      }
    }
    return false;
  }

  /**
   * 获取脏节点统计信息
   */
  getDirtyStats(): {
    total: number;
    high: number;
    normal: number;
    low: number;
  } {
    const dirtyNodes = this.getDirtyNodesSinceLastRender();
    let high = 0;
    let low = 0;

    for (const nodeId of dirtyNodes) {
      if (this.highPriorityNodes.has(nodeId)) {
        high++;
      }
    }

    return {
      total: dirtyNodes.length,
      high,
      normal: dirtyNodes.length - high - low,
      low,
    };
  }

  attach(renderContext: RenderContext): void {
    super.attach(renderContext);

    const api: DirtyAPI = {
      markDirty: (nodeId, changedKeys) => this.markDirty(nodeId, changedKeys),
      markDirtyBatch: (nodeIds, changedKeys) => this.markDirtyBatch(nodeIds, changedKeys),
      getDirtyState: (nodeId) => this.getDirtyState(nodeId),
      getVersion: (nodeId) => this.getVersion(nodeId),
      getGroupVersion: (nodeId, group) => this.getGroupVersion(nodeId, group),
      getContentKey: (nodeId) => this.getContentKey(nodeId),
      getTransformKey: (nodeId) => this.getTransformKey(nodeId),
      getSubtreeVersion: (nodeId, childIds) => this.getSubtreeVersion(nodeId, childIds),
      isSubtreeDirty: (nodeId, childIds, cachedVersion) =>
        this.isSubtreeDirty(nodeId, childIds, cachedVersion),
      getDirtyChildIds: (childIds, cachedVersions) =>
        this.getDirtyChildIds(childIds, cachedVersions),
      isGroupDirty: (nodeId, group, sinceVersion) => this.isGroupDirty(nodeId, group, sinceVersion),
      clearDirty: (nodeId) => this.clearDirty(nodeId),
      clearAllDirty: () => this.clearAllDirty(),
      getPropertyGroup: (key) => this.getPropertyGroup(key),
      // 渲染调度 API
      setHighPriority: (nodeId) => this.setHighPriority(nodeId),
      clearHighPriority: (nodeId) => this.clearHighPriority(nodeId),
      clearAllHighPriority: () => this.clearAllHighPriority(),
      isHighPriority: (nodeId) => this.isHighPriority(nodeId),
      getDirtyNodesSinceLastRender: () => this.getDirtyNodesSinceLastRender(),
      getSortedDirtyNodes: (viewportNodeIds) => this.getSortedDirtyNodes(viewportNodeIds),
      markRenderComplete: () => this.markRenderComplete(),
      hasDirtyNodes: () => this.hasDirtyNodes(),
      getDirtyStats: () => this.getDirtyStats(),
    };

    Object.assign(renderContext.api, api);
  }

  dispose(): void {
    this.dirtyMap.clear();
    this.propertyGroupCache.clear();
    this.renderQueue.clear();
    this.highPriorityNodes.clear();
    super.dispose();
  }
}

/**
 * 从节点获取版本信息的辅助函数
 */
export function getNodeVersion(node: NodeLike, dirtyProvider: DirtyProvider): string {
  return dirtyProvider.getVersion(node.id);
}

/**
 * 生成缓存键的辅助函数
 * 可以选择性地使用特定分组的版本
 */
export function generateCacheKey(
  node: NodeLike,
  dirtyProvider: DirtyProvider,
  groups?: DirtyGroup[]
): string {
  if (!groups || groups.length === 0) {
    return dirtyProvider.getVersion(node.id);
  }

  return groups.map((g) => dirtyProvider.getGroupVersion(node.id, g)).join('-');
}

declare module '../render/context/createRenderContext' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface RendererAPI extends DirtyAPI {}
}
